import PropTypes from 'prop-types';
import React from 'react';

import { ButtonGroup } from '@/design-system/atoms/ButtonGroup';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';
import { Text } from '@/design-system/atoms/Text';
import { Title } from '@/design-system/atoms/Title';
import { arrayNotEmpty } from '@/utils/arrayNotEmpty';

import styles from './CardText.module.scss';

/**
 * The Card Text Component is used to display a title, description, CTA buttons,
 * and interchangable Awards, Testimonials or Related List widgets.
 */
const CardText = ({
    eyebrow,
    title,
    description,
    ctas,
    children,
    placeholderComponent,
    htmlId,
    mobileWidgetTop,
    ...props
}) => {
    return (
        <div className={styles[`card-text`]} {...props} id={htmlId} data-component-name="card-text">
            <ContentContainer>
                <Grid
                    verticalAlignment="center"
                    row={{ sm: 2, md: 1, lg: 1 }}
                    className={styles[`card-text__grid`]}
                    isContainerQuery
                >
                    <GridColumn
                        className={styles[`card-text__content`]}
                        colSpan={{ sm: 4, md: 3, lg: 8 }}
                        rowStart={{ sm: mobileWidgetTop ? 2 : 1, md: 1, lg: 1 }}
                        rowSpan={{ sm: 1, md: 1, lg: 1 }}
                    >
                        {eyebrow && (
                            <Text tag={Text.TAG.P} variant={Text.VARIANT.E1} content={eyebrow} />
                        )}
                        <Title tag={Title.TAG.H2} variant={Title.VARIANT.H3} content={title} />
                        <RichTextEditor
                            text={description}
                            className={styles[`card-text__rich-text`]}
                        />
                        {arrayNotEmpty(ctas) && (
                            <ButtonGroup
                                buttons={ctas}
                                className={styles[`card-text__buttons`]}
                                fullWidth={{ md: true }}
                                analytics={{
                                    componentName: 'Card and Text',
                                    componentTitle: title,
                                }}
                            />
                        )}
                    </GridColumn>
                    <GridColumn
                        className={styles[`card-text__widget`]}
                        colSpan={{ sm: 4, md: 3, lg: 4 }}
                        rowStart={{ sm: mobileWidgetTop ? 1 : 2, md: 1, lg: 1 }}
                        rowSpan={{ sm: 1, md: 1, lg: 1 }}
                    >
                        {children ? children : placeholderComponent}
                    </GridColumn>
                </Grid>
            </ContentContainer>
        </div>
    );
};

CardText.propTypes = {
    /**
     * Optional Eyebrow Text
     */
    eyebrow: PropTypes.string,
    /**
     * Required Title Text
     */
    title: PropTypes.string.isRequired,
    /**
     * Required Description RTE Body Text
     */
    description: PropTypes.node.isRequired,
    /**
     * Optional CTAs
     */
    ctas: ButtonGroup.propTypes.buttons,
    /**
     * Required Widget, Awards, Testimonials or Related List
     */
    children: PropTypes.node,
    /**
     * Unique component ID
     * */
    htmlId: PropTypes.string,
};
export default CardText;
